import React, { useRef, useState } from "react";
import styles from "./FormTemplateListItem.module.scss";
import { FiPlus, FiEdit, FiX, FiTrash, FiDownload } from "react-icons/fi";
import {
	Button,
	Colors,
	DialogContainer,
	Modal,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	TextFieldWrapper,
} from "@factorearth/component-library";
import { FormTemplate, User } from "db/definitions";
import { downloadFormTemplateJSON } from "db/queries/formTemplateQueries";

interface Props {
	color: Colors;
	handleDeleteTemplate: (id: string) => Promise<boolean>;
	handleEditTemplate: (data: { id: string }) => Promise<void>;
	online: boolean;
	setupNewVersionDerived: (id: string, versionData: { versionName: string; publish: boolean }) => Promise<void>;
	template: FormTemplate;
	userDoc: User;
}

function FormTemplateListItem(props: Props) {
	const { color, handleDeleteTemplate, handleEditTemplate, online, setupNewVersionDerived, template, userDoc } = props;

	function handleEditTemplateClick() {
		handleEditTemplate({ id: template.id });
	}

	return (
		<div
			className={styles.wrapper}
			style={{
				border: `2px solid ${color.text.secondary}`,
			}}
			key={template.id}>
			<div
				style={{
					background: "none",
				}}
				className={styles.templateItem}>
				{userDoc?.group === template.group || userDoc?.group === "FactorEarth" ? (
					<div
						data-test={`edit-form-template-${template.id}`}
						onClick={handleEditTemplateClick}
						className={styles.template_item_expand}
						role="button"
						style={{
							color: color.text.tertiary,
						}}>
						<FiEdit />
						Edit
					</div>
				) : null}
				{online && (userDoc?.group === template.group || userDoc?.group === "FactorEarth") ? (
					<DownloadTemplateButton color={color} id={template.id} templateName={template.templateName || template.template} online={online} />
				) : null}
				<DeriveTemplateButton color={color} id={template.id} setupVersionDerived={setupNewVersionDerived} />
				<div style={{ color: color.text.primary }} className={styles.template_item_name}>
					{template.templateName || template.template}
					{template.versionName ? ` - ${template.versionName}` : null}
				</div>
				{/* NEED PERMS HERE */}
				{online ? <DeleteTemplateButton color={color} id={template.id} handleDeleteTemplate={handleDeleteTemplate} /> : null}
			</div>
		</div>
	);
}

interface DeriveTemplateButtonProps {
	color: Colors;
	id: string;
	setupVersionDerived: (id: string, { versionName, publish }: { versionName: string; publish: boolean }) => Promise<void>;
}

function DeriveTemplateButton(props: DeriveTemplateButtonProps) {
	const { color, id, setupVersionDerived } = props;

	const [open, setOpen] = useState(false);
	const [input, setInput] = useState("");

	async function submit() {
		if (input) {
			setOpen(false);
			await setupVersionDerived(id, { versionName: input, publish: false });
		}
	}

	return (
		<>
			<Button colorPalette={color} style={{ width: "150px" }} onClick={() => setOpen(true)} variant="filled" icon={<FiPlus />} label="New Version" />
			<Modal open={open} colorPalette={color} setOpen={setOpen}>
				<DialogContainer>
					<ModalTitleContainer>
						<ModalTitle>Create Derived Template</ModalTitle>
						<FiX onClick={() => setOpen(false)} size={24} />
					</ModalTitleContainer>
					<ModalInputBox>
						<ModalHeading>New Version Required. Please Enter a Name for this Version</ModalHeading>
						<TextFieldWrapper
							colorPalette={color}
							value={input}
							onChange={(e) => {
								setInput(e.target.value);
							}}
						/>
					</ModalInputBox>
					<ModalFooter>
						<Button colorPalette={color} variant="filled" label="Create" onClick={submit} />
						<Button colorPalette={color} variant="destructive" label="Cancel" onClick={() => setOpen(false)} />
					</ModalFooter>
				</DialogContainer>
			</Modal>
		</>
	);
}

interface DeleteTemplateButtonProps {
	color: Colors;
	id: string;
	handleDeleteTemplate: (id: string) => Promise<boolean>;
}

function DeleteTemplateButton(props: DeleteTemplateButtonProps) {
	const { color, id, handleDeleteTemplate } = props;

	const [open, setOpen] = useState(false);
	const [loader, setLoader] = useState(false);

	async function handleDeleteTemplateClick(id: string) {
		setLoader(true);
		const success = await handleDeleteTemplate(id);
		if (success) {
			setOpen(false);
			setLoader(false);
		}
	}

	return (
		<>
			<Button
				colorPalette={color}
				variant="destructive"
				className={styles.project_button}
				onClick={() => setOpen(true)}
				icon={<FiTrash />}
				label="Delete"
				data-test={`delete-form-template-${id}`}
			/>
			<Modal open={open} colorPalette={color} setOpen={setOpen}>
				<DialogContainer>
					<ModalTitleContainer>
						<ModalTitle>Delete Template</ModalTitle>
						<FiX onClick={() => setOpen(false)} size={24} />
					</ModalTitleContainer>
					{!loader ? (
						<>
							<ModalInputBox>
								<ModalHeading>Are you sure you want to delete this template?</ModalHeading>
							</ModalInputBox>
							<ModalFooter>
								<Button
									colorPalette={color}
									variant="filled"
									label="Yes"
									onClick={() => handleDeleteTemplateClick(id)}
									data-test={`confirm-delete-template-button`}
								/>
								<Button colorPalette={color} variant="destructive" label="Cancel" onClick={() => setOpen(false)} />
							</ModalFooter>
						</>
					) : (
						<>
							<ModalInputBox>
								<ModalHeading>Deleting Template...</ModalHeading>
							</ModalInputBox>
							<ModalFooter>Please wait.</ModalFooter>
						</>
					)}
				</DialogContainer>
			</Modal>
		</>
	);
}

interface DownloadTemplateButtonProps {
	color: Colors;
	id: string;
	templateName: string;
	online: boolean;
}

function DownloadTemplateButton(props: DownloadTemplateButtonProps) {
	const { color, id, templateName, online } = props;

	async function handleDownload() {
		const data = await downloadFormTemplateJSON(id, online);
		if (!data) return;
		const json = JSON.stringify(data, null, 2);
		const blob = new Blob([json], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${templateName}.json`;
		a.click();
		URL.revokeObjectURL(url);
	}

	return <Button colorPalette={color} variant="outlined" icon={<FiDownload />} label="Download" onClick={handleDownload} />;
}

export default FormTemplateListItem;
